import React from 'react';
import type { Product } from '../../types/product';
import { X } from 'lucide-react';

interface ProductFormProps {
  initialData?: Product;
  onSubmit: (data: Product) => void;
  onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const data = {
      id: initialData?.id || crypto.randomUUID(),
      name: formData.get('name') as string,
      internalCode: formData.get('internalCode') as string,
      barcode: formData.get('barcode') as string,
      category: formData.get('category') as string,
      subcategory: formData.get('subcategory') as string,
      unit: formData.get('unit') as string,
      costPrice: parseFloat(formData.get('costPrice') as string),
      sellingPrice: parseFloat(formData.get('sellingPrice') as string),
      minStock: parseInt(formData.get('minStock') as string),
      currentStock: parseInt(formData.get('currentStock') as string),
      status: (formData.get('status') as 'active' | 'inactive') || 'active',
      createdAt: initialData?.createdAt || new Date(),
      updatedAt: new Date()
    };
    onSubmit(data as Product);
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="flex justify-between items-center p-6 border-b">
        <h2 className="text-xl font-semibold">
          {initialData ? 'Editar Produto' : 'Novo Produto'}
        </h2>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Produto
            </label>
            <input
              type="text"
              name="name"
              defaultValue={initialData?.name}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código Interno
            </label>
            <input
              type="text"
              name="internalCode"
              defaultValue={initialData?.internalCode}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Código de Barras
            </label>
            <input
              type="text"
              name="barcode"
              defaultValue={initialData?.barcode}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categoria
            </label>
            <input
              type="text"
              name="category"
              defaultValue={initialData?.category}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategoria
            </label>
            <input
              type="text"
              name="subcategory"
              defaultValue={initialData?.subcategory}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unidade
            </label>
            <input
              type="text"
              name="unit"
              defaultValue={initialData?.unit}
              required
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preço de Custo
            </label>
            <input
              type="number"
              name="costPrice"
              defaultValue={initialData?.costPrice}
              required
              step="0.01"
              min="0"
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Preço de Venda
            </label>
            <input
              type="number"
              name="sellingPrice"
              defaultValue={initialData?.sellingPrice}
              required
              step="0.01"
              min="0"
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estoque Mínimo
            </label>
            <input
              type="number"
              name="minStock"
              defaultValue={initialData?.minStock}
              required
              min="0"
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Estoque Atual
            </label>
            <input
              type="number"
              name="currentStock"
              defaultValue={initialData?.currentStock}
              required
              min="0"
              className="w-full border rounded-md p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              defaultValue={initialData?.status || 'active'}
              className="w-full border rounded-md p-2"
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border rounded-md hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            {initialData ? 'Salvar Alterações' : 'Criar Produto'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;